import {
    cssVar,
    Flex,
    VStack,
    Button,
    Checkbox,
    IconButton,
    CheckboxGroup,
    Spacer,
    Text,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import { DebouncedFunc } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { htmlToText } from 'html-to-text';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import {
    $generateHtmlFromNodes,
    ClearEditorPlugin,
    CommandListenersPlugin,
    ContentEditable,
    EditorControl,
    EditorControlPlugin,
    FlagNodes,
    FlagPlugin,
    HistoryPlugin,
    LexicalComposer,
    LexicalErrorBoundary,
    MaxLengthPlugin,
    OnChangePlugin,
    PlainTextPlugin,
    RestoreStatePlugin,
} from '../../common/components/lexical';
import { OnLexicalError } from '../../common/components/lexical/types';
import { CrossClassInformation } from '../types';
import { useNarrativeOverwriteSensor } from '../hooks/use-narrative-overwrite-sensor';
import { EditorState } from 'lexical';
import { useKeyRecorder } from '../key-recorder/use-key-recorder';

export const DEFAULT_NARRATIVE_INPUT_FIELD_NAME = 'narrative-input';
const TAB_KEY: string = 'Tab';
export type NarrativeInputFieldValues = FieldValues;
interface NarrativeInputProps extends React.ComponentProps<'div'> {
    isNewEvent?: boolean;
    fieldName?: string;
    agencyEventId: number;
    control: Control<NarrativeInputFieldValues, unknown>;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    isHighPriority?: boolean;
    onLexicalError?: OnLexicalError;
    // Agency type label -> globalAgencyAttrId
    crossClassInformation?: CrossClassInformation;
    onReplaceVehicles?: (vehicles: string[]) => void | DebouncedFunc<(vehicles: string[]) => void>;
    onSubmit?: () => void;
    setIsHighPriority?: (value: boolean) => void;
    editorState?: EditorState;
    storeEditorState?: (value: EditorState) => void;
    mode?: 'read-only';
    isFirstResponder?: boolean;
}

export const NarrativeInput = ({
    isNewEvent,
    fieldName,
    agencyEventId,
    className,
    control,
    editorControlRef,
    isHighPriority,
    onLexicalError,
    onReplaceVehicles,
    onSubmit,
    setIsHighPriority,
    crossClassInformation,
    editorState,
    storeEditorState,
    mode,
    isFirstResponder,
    ...props
}: NarrativeInputProps): React.JSX.Element => {
    const contentRef = useRef<string>('');
    const canSetHighPriority = useEnabledFeatures()?.['usersCanChangeUpdatePriority'] ?? false;
    const showHighPriorityToggle = !isNewEvent && canSetHighPriority;

    const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [selectedXClassOptions, setXClassOpts] = useState<(number | string)[]>([]);

    const { narrativeOverwriteSensor } = useNarrativeOverwriteSensor(agencyEventId);
    const { keyRecorder } = useKeyRecorder();

    useEffect(() => {
        editorControlRef.current?.setEditable(mode !== 'read-only');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        if (crossClassInformation) {
            setXClassOpts(
                crossClassInformation.crossClassOptions.map((option) => option.value.toString())
            );
        }
    }, [crossClassInformation]);

    const labelMap = useMemo(
        () =>
            crossClassInformation?.crossClassOptions
                ? Object.fromEntries(
                      crossClassInformation.crossClassOptions.map((o) => [o.value, o.label])
                  )
                : undefined,
        [crossClassInformation?.crossClassOptions]
    );

    function getFocusColor() {
        if (!isFocused) {
            return cssVar('arc.colors.control.border.default');
        }
        if (isHighPriority) {
            return 'var(--arc-colors-urgent-default)';
        }
        return cssVar('arc.colors.selected.default');
    }

    const buttonStyle =
        canSetHighPriority && isHighPriority
            ? {
                  background: 'var(--arc-colors-urgent-default)',
                  color: 'var(--arc-colors-urgent-content)',
              }
            : {};

    const buttonHoverStyle =
        canSetHighPriority && isHighPriority
            ? {
                  background: 'var(--arc-colors-urgent-hover) !important',
              }
            : {};

    function onSend() {
        if (
            onSubmit &&
            editorControlRef.current?.isEditable() &&
            contentRef.current &&
            contentRef.current.trim() !== ''
        ) {
            onSubmit();
            editorControlRef.current?.setEditable(false);
        }
    }

    function handleCursorFocusOnTab() {
        const lastEvent = keyRecorder?.lastEvent;
        if (lastEvent?.key === TAB_KEY) {
            editorControlRef.current?.focus();
        }
    }

    function handleFocusOnBottomSpace() {
        setIsFocused(true);
        editorControlRef.current?.focus();
    }

    return (
        <VStack
            data-testid="add-message"
            display="inline-flex"
            alignItems="flex-start"
            paddingBlock="10px"
            paddingInline="var(--arc-space-3)"
            borderRadius="var(--arc-radii-md)"
            backgroundColor="surface.foreground"
            borderWidth={1}
            width="100%"
            transitionDuration="var(--arc-transition-duration-normal)"
            borderColor={getFocusColor()}
            boxShadow={isFocused ? `0 0 0 1px ${getFocusColor()}` : '0 0 0 0'}
            data-inputid={`${fieldName ?? DEFAULT_NARRATIVE_INPUT_FIELD_NAME}-${agencyEventId}`}
            {...props}
        >
            <Controller
                control={control}
                name={`${fieldName ?? DEFAULT_NARRATIVE_INPUT_FIELD_NAME}`}
                render={({ field: { onChange, onBlur } }) => {
                    return (
                        <LexicalComposer
                            initialConfig={{
                                onError(error) {
                                    onLexicalError?.({
                                        error,
                                        agencyEventId,
                                        message: 'Additional infos input, Lexical error',
                                    });
                                },
                                nodes: [...FlagNodes],
                                namespace: 'additional-infos',
                            }}
                        >
                            <>
                                <PlainTextPlugin
                                    contentEditable={
                                        <ContentEditable
                                            style={{
                                                fontSize: isFirstResponder
                                                    ? 'var(--arc-fontSizes-lg)'
                                                    : 'var(--arc-fontSizes-sm)',
                                                width: '100%',
                                                outline: 'none',
                                                borderStyle: '0',
                                                maxHeight: 'var(--arc-space-24)',
                                                overflow: 'auto',
                                                color: isReadOnly
                                                    ? 'text.tertiary' //TODO:THEME: What is the proper read only color?
                                                    : 'text.primary',
                                            }}
                                            data-testid="ADDITIONAL_NARRATIVE_INPUT"
                                        />
                                    }
                                    placeholder={null}
                                    ErrorBoundary={LexicalErrorBoundary}
                                />
                                {/* we use RestoreStatePlugin instead of setting editorState in initialConfig so that OnChangePlugin fires */}
                                <RestoreStatePlugin editorState={editorState} />
                                <OnChangePlugin
                                    onChange={(editorState, editor) => {
                                        // This hooks up the Lexical editor to the React Hook Form. When there's a change
                                        // to the Lexical editor, this callback is fired, the Lexical (node-based) data is
                                        // converted to plain text and sent to React Hook Form via its onChange callback.
                                        editorState.read(() => {
                                            const html = $generateHtmlFromNodes(editor, null);
                                            const text = htmlToText(html, {
                                                preserveNewlines: true,
                                                whitespaceCharacters: '',
                                            });
                                            narrativeOverwriteSensor({
                                                prevContent: contentRef.current,
                                                newContent: text,
                                            });
                                            contentRef.current = text;
                                            onChange(text);
                                        });
                                        storeEditorState?.(editorState.clone());
                                    }}
                                    ignoreSelectionChange
                                />
                                <CommandListenersPlugin
                                    onBlur={() => {
                                        setIsFocused(false);
                                        onBlur();
                                    }}
                                    onEditable={(isEditable) => {
                                        if (isEditable) {
                                            setIsReadOnly(false);
                                        } else {
                                            setIsReadOnly(true);
                                        }
                                    }}
                                    onEnterDown={onSend}
                                    onFocus={() => {
                                        setIsFocused(true);
                                        handleCursorFocusOnTab();
                                    }}
                                    allowEnterNewLine={isNewEvent}
                                />
                                <EditorControlPlugin ref={editorControlRef} />
                                {/* FlagPlugin renders the caution and plate flags in the editor */}
                                <FlagPlugin onReplaceVehicles={onReplaceVehicles} />
                                <MaxLengthPlugin maxLength={1024 * 64} />
                                {/* ClearEditorPlugin add support to clear the editor via dispatching CLEAR_EDITOR_COMMAND */}
                                <ClearEditorPlugin />
                                {/* HistoryPlugin allows undo/redo keypress in the editor */}
                                <HistoryPlugin />
                            </>
                        </LexicalComposer>
                    );
                }}
            />
            <Flex w="100%">
                {showHighPriorityToggle && (
                    <IconButton
                        style={buttonStyle}
                        _hover={buttonHoverStyle}
                        data-testid="toggle-urgent-message"
                        icon="ExclamationPoint"
                        aria-label="Toggle urgent message"
                        onClick={() => {
                            if (editorControlRef.current?.isEditable() && canSetHighPriority) {
                                setIsHighPriority?.(!isHighPriority);
                                editorControlRef.current?.focus();
                            }
                        }}
                    />
                )}
                <Spacer style={{ flex: 1 }} onClick={handleFocusOnBottomSpace} />
                {!isNewEvent && (
                    <>
                        {crossClassInformation && (
                            <Controller
                                name="cross-class-options"
                                control={control}
                                render={(field) => {
                                    return (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    trailingVisual="Open"
                                                    data-testid="EVENT_PANEL_NARRATIVE_TOGGLE"
                                                >
                                                    {selectedXClassOptions
                                                        .map((c) => labelMap?.[c])
                                                        .join(', ')}
                                                </Button>
                                            </PopoverTrigger>

                                            <PopoverContent>
                                                <Text variant="headingXs">Send to</Text>
                                                <CheckboxGroup
                                                    mt={1}
                                                    direction="column"
                                                    name="cross-class-options"
                                                    value={selectedXClassOptions}
                                                    onChange={(v) => {
                                                        setXClassOpts(v);
                                                        field.field.onChange(
                                                            v.map((o) => parseInt(o as string, 10))
                                                        );
                                                    }}
                                                >
                                                    {crossClassInformation?.crossClassOptions.map(
                                                        (option) => (
                                                            <Checkbox
                                                                key={option.label}
                                                                value={`${option.value}`}
                                                                defaultChecked
                                                                data-testid={`CROSS_CLASS_OPTION_${option.label}`}
                                                                isDisabled={
                                                                    option.value ===
                                                                    crossClassInformation.agencyType
                                                                }
                                                            >
                                                                {option.label}
                                                            </Checkbox>
                                                        )
                                                    )}
                                                </CheckboxGroup>
                                            </PopoverContent>
                                        </Popover>
                                    );
                                }}
                            />
                        )}
                        <IconButton
                            style={buttonStyle}
                            _hover={buttonHoverStyle}
                            variant="solid"
                            data-testid="send-narrative-button"
                            icon="Send"
                            aria-label="Send narrative"
                            onClick={onSend}
                            isLoading={!isNewEvent && isReadOnly}
                        />
                    </>
                )}
            </Flex>
        </VStack>
    );
};
